import { Component, computed, input } from '@angular/core';
import type { RiskTone } from '../../../core/models/types';

@Component({
  selector: 'uw-progress-ring',
  standalone: true,
  template: `
    <div class="relative inline-flex" [style.width.px]="size()" [style.height.px]="size()" role="progressbar" [attr.aria-valuenow]="value()" aria-valuemin="0" aria-valuemax="100" [attr.aria-label]="label()">
      <svg [attr.width]="size()" [attr.height]="size()" [style.transform]="'rotate(-90deg)'">
        <circle [attr.cx]="half()" [attr.cy]="half()" [attr.r]="radius()" fill="none" [attr.stroke-width]="stroke()" class="stroke-surface-sunken" />
        <circle
          [attr.cx]="half()"
          [attr.cy]="half()"
          [attr.r]="radius()"
          fill="none"
          [attr.stroke-width]="stroke()"
          stroke-linecap="round"
          [class]="strokeClass()"
          [attr.stroke-dasharray]="circumference()"
          [attr.stroke-dashoffset]="offset()"
        />
      </svg>
      <div class="absolute inset-0 flex flex-col items-center justify-center">
        <span class="typo-mono font-bold" [style.font-size.px]="size() * 0.22">{{ value() }}</span>
        @if (label()) {
          <span class="typo-label" [style.font-size.px]="9">{{ label() }}</span>
        }
      </div>
    </div>
  `,
})
export class UwProgressRing {
  readonly value = input(0);
  readonly size = input(88);
  readonly stroke = input(8);
  readonly tone = input<RiskTone>('warning');
  readonly label = input('');

  protected readonly clamped = computed(() => Math.min(100, Math.max(0, this.value())));
  protected readonly radius = computed(() => (this.size() - this.stroke()) / 2);
  protected readonly half = computed(() => this.size() / 2);
  protected readonly circumference = computed(() => 2 * Math.PI * this.radius());
  protected readonly offset = computed(() => this.circumference() - (this.clamped() / 100) * this.circumference());

  protected readonly strokeClass = computed(() => {
    const tones: Record<string, string> = {
      success: 'stroke-success',
      warning: 'stroke-warning',
      danger: 'stroke-danger',
      info: 'stroke-info',
      neutral: 'stroke-text-muted',
      ai: 'stroke-ai',
    };
    return tones[this.tone()] ?? tones['warning'];
  });
}
